import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { ThemeContext } from "../../App";
import axios from "axios";
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Chip,
    TextField,
    InputAdornment,
    Tooltip,
    CircularProgress,
    Snackbar,
    Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import styles from "./Console.module.css";

const Console = () => {
    const { isDarkMode } = useContext(ThemeContext);
    const [logs, setLogs] = useState<string[]>([]);
    const [filteredLogs, setFilteredLogs] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success" as "success" | "error" | "info" | "warning",
    });
    const [activeFilters, setActiveFilters] = useState<string[]>([]);
    const logEndRef = useRef<HTMLDivElement>(null);

    // Available log levels for filtering
    const logLevels = ["INFO", "ERROR", "WARNING", "DEBUG", "ARDUINO"];

    // Fetch logs from the server
    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get("/logging/get", {
                headers: { Accept: "application/json" },
            });

            if (response.data && response.data.logs) {
                setLogs(response.data.logs);
                applyFiltersAndSearch(
                    response.data.logs,
                    activeFilters,
                    searchTerm
                );
            }
        } catch (error) {
            console.error("Failed to fetch logs:", error);
            setSnackbar({
                open: true,
                message: "Failed to load logs from server",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [activeFilters, searchTerm]);

    // Clear all logs
    const clearLogs = async () => {
        try {
            setLoading(true);
            await axios.delete("/logging/delete");
            setLogs([]);
            setFilteredLogs([]);
            setSnackbar({
                open: true,
                message: "All logs have been cleared",
                severity: "success",
            });
        } catch (error) {
            console.error("Failed to clear logs:", error);
            setSnackbar({
                open: true,
                message: "Failed to clear logs",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    // Toggle auto-refresh
    const toggleAutoRefresh = () => {
        setAutoRefresh(!autoRefresh);
    };

    // Copy logs to clipboard
    const copyToClipboard = () => {
        const textToCopy = filteredLogs.join("\n");
        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setSnackbar({
                    open: true,
                    message: "Logs copied to clipboard",
                    severity: "success",
                });
            })
            .catch((err) => {
                console.error("Failed to copy logs:", err);
                setSnackbar({
                    open: true,
                    message: "Failed to copy logs to clipboard",
                    severity: "error",
                });
            });
    };

    // Handle filter toggling
    const toggleFilter = (level: string) => {
        const newFilters = activeFilters.includes(level)
            ? activeFilters.filter((f) => f !== level)
            : [...activeFilters, level];

        setActiveFilters(newFilters);
        applyFiltersAndSearch(logs, newFilters, searchTerm);
    };

    // Apply filters and search to logs
    const applyFiltersAndSearch = (
        logArray: string[],
        filters: string[],
        search: string
    ) => {
        let result = logArray;

        // Apply level filters if any are selected
        if (filters.length > 0) {
            result = result.filter((log) =>
                filters.some((filter) => log.includes(`[${filter}]`))
            );
        }

        // Apply search term if any
        if (search) {
            result = result.filter((log) =>
                log.toLowerCase().includes(search.toLowerCase())
            );
        }

        setFilteredLogs(result);
    };

    // Handle search input
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchTerm(value);
        applyFiltersAndSearch(logs, activeFilters, value);
    };

    // Scroll to bottom when logs update
    useEffect(() => {
        logEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [filteredLogs]);

    // Set up auto-refresh
    useEffect(() => {
        fetchLogs();

        let intervalId: NodeJS.Timeout | null = null;
        if (autoRefresh) {
            intervalId = setInterval(fetchLogs, 100);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [autoRefresh, fetchLogs]);

    // Format log entry with color coding by level
    const formatLogEntry = (log: string) => {
        if (log.includes("[INFO]")) {
            return <span className={styles.infoLog}>{log}</span>;
        } else if (log.includes("[ERROR]")) {
            return <span className={styles.errorLog}>{log}</span>;
        } else if (log.includes("[WARNING]")) {
            return <span className={styles.warningLog}>{log}</span>;
        } else if (log.includes("[DEBUG]")) {
            return <span className={styles.debugLog}>{log}</span>;
        } else if (log.includes("[ARDUINO]")) {
            return <span className={styles.arduinoLog}>{log}</span>;
        }
        return <span>{log}</span>;
    };

    // Get icon for log level
    const getLevelIcon = (level: string) => {
        switch (level) {
            case "INFO":
                return <InfoIcon fontSize="small" />;
            case "ERROR":
                return <ErrorIcon fontSize="small" />;
            case "WARNING":
                return <WarningIcon fontSize="small" />;
            case "DEBUG":
                return <InfoIcon fontSize="small" />;
            case "ARDUINO":
                return <AutorenewIcon fontSize="small" />;
            default:
                return undefined;
        }
    };

    return (
        <Box
            className={`${styles.container} ${
                isDarkMode ? styles.darkTheme : styles.lightTheme
            }`}>
            <Paper
                elevation={3}
                className={styles.header}
                sx={{
                    backgroundImage:
                        "linear-gradient(135deg, #0077b6, #023e8a)",
                }}>
                <Typography variant="h4" className={styles.title}>
                    Server Console
                </Typography>
                <Typography variant="subtitle1" className={styles.subtitle}>
                    View and manage application logs
                </Typography>
            </Paper>

            <Box className={styles.toolbarContainer}>
                <Box className={styles.searchBox}>
                    <TextField
                        placeholder="Search logs..."
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Box className={styles.filterContainer}>
                    <Box className={styles.filterHeader}>
                        <FilterListIcon fontSize="small" />
                        <Typography variant="body2">Filters:</Typography>
                    </Box>

                    <Box className={styles.chipContainer}>
                        {logLevels.map((level) => (
                            <Chip
                                key={level}
                                icon={getLevelIcon(level)}
                                label={level}
                                size="small"
                                color={
                                    activeFilters.includes(level)
                                        ? "primary"
                                        : "default"
                                }
                                onClick={() => toggleFilter(level)}
                                className={styles.filterChip}
                            />
                        ))}
                    </Box>
                </Box>

                <Box className={styles.actionsContainer}>
                    <Tooltip title="Refresh logs">
                        <IconButton onClick={fetchLogs} disabled={loading}>
                            <RefreshIcon
                                className={loading ? styles.spinning : ""}
                            />
                        </IconButton>
                    </Tooltip>

                    <Tooltip
                        title={
                            autoRefresh
                                ? "Stop auto-refresh"
                                : "Auto-refresh logs"
                        }>
                        <IconButton
                            onClick={toggleAutoRefresh}
                            color={autoRefresh ? "primary" : "default"}>
                            <AutorenewIcon
                                className={autoRefresh ? styles.spinning : ""}
                            />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Copy logs">
                        <IconButton
                            onClick={copyToClipboard}
                            disabled={filteredLogs.length === 0}>
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Clear logs">
                        <IconButton
                            onClick={clearLogs}
                            disabled={loading || logs.length === 0}
                            color="error">
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Paper className={styles.terminalContainer} elevation={3}>
                {loading && logs.length === 0 ? (
                    <Box className={styles.loadingContainer}>
                        <CircularProgress size={40} />
                        <Typography>Loading logs...</Typography>
                    </Box>
                ) : filteredLogs.length === 0 ? (
                    <Box className={styles.emptyState}>
                        <Typography variant="body1" color="textSecondary">
                            {logs.length === 0
                                ? "No logs available. Server logs will appear here."
                                : "No logs match your filters."}
                        </Typography>
                    </Box>
                ) : (
                    <Box className={styles.terminal}>
                        {filteredLogs.map((log, index) => (
                            <div key={index} className={styles.logEntry}>
                                {formatLogEntry(log)}
                            </div>
                        ))}
                        <div ref={logEndRef} />
                    </Box>
                )}
            </Paper>

            <Box className={styles.statsContainer}>
                <Typography variant="body2">
                    Showing {filteredLogs.length} of {logs.length} logs
                    {activeFilters.length > 0 &&
                        ` (filtered by: ${activeFilters.join(", ")})`}
                    {searchTerm && ` (search: "${searchTerm}")`}
                </Typography>
                {autoRefresh && (
                    <Chip
                        size="small"
                        color="primary"
                        label="Auto-refresh on"
                        className={styles.autoRefreshBadge}
                        icon={<AutorenewIcon className={styles.spinning} />}
                    />
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() =>
                    setSnackbar((prev) => ({ ...prev, open: false }))
                }>
                <Alert
                    onClose={() =>
                        setSnackbar((prev) => ({ ...prev, open: false }))
                    }
                    severity={snackbar.severity}
                    variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Console;
